import api from './api';

// Requirement service for managing requirements and checkpoints

// Feature Requirements
export const getNextFeatureRequirementId = async (featureId) => {
  const response = await api.get(`/requirements/features/${featureId}/next-id`);
  return response.data;
};

export const createRequirement = async (featureId, requirementData) => {
  const response = await api.post(`/requirements/features/${featureId}/requirements`, requirementData);
  return response.data;
};

export const updateRequirement = async (featureId, logicalId, requirementData) => {
  const response = await api.put(`/requirements/features/${featureId}/requirements/${logicalId}`, requirementData);
  return response.data;
};

export const updateRequirementStatus = async (featureId, logicalId, status) => {
  const response = await api.put(`/requirements/features/${featureId}/requirements/${logicalId}/status`, { status });
  return response.data;
};

export const listRequirements = async (featureId, statusFilter = null) => {
  const params = statusFilter ? { status: statusFilter } : {};
  const response = await api.get(`/requirements/features/${featureId}/requirements`, { params });
  return response.data;
};

export const deleteRequirement = async (featureId, logicalId) => {
  await api.delete(`/requirements/features/${featureId}/requirements/${logicalId}`);
};

export const markRequirementForDeletion = async (featureId, logicalId) => {
  const response = await api.post(`/requirements/features/${featureId}/requirements/${logicalId}/mark-deleted`);
  return response.data;
};

// Checkpoints (Project-wide)
export const createCheckpoint = async (projectId, checkpointData) => {
  const response = await api.post(`/requirements/projects/${projectId}/checkpoints`, checkpointData);
  return response.data;
};

export const listCheckpoints = async (projectId) => {
  const response = await api.get(`/requirements/projects/${projectId}/checkpoints`);
  return response.data;
};

export const getDiff = async (featureId, fromCommit, toCommit) => {
  const params = { from_commit: fromCommit, to_commit: toCommit };
  const response = await api.get(`/requirements/features/${featureId}/diff`, { params });
  return response.data;
};

export const getRequirementTree = async (featureId, commitId) => {
  const response = await api.get(`/requirements/features/${featureId}/tree/${commitId}`);
  return response.data;
};

// Branches
export const createBranch = async (featureId, branchData) => {
  const response = await api.post(`/requirements/features/${featureId}/branches`, branchData);
  return response.data;
};

// Global Requirements (Project-level)
export const getNextProjectRequirementId = async (projectId) => {
  const response = await api.get(`/requirements/projects/${projectId}/next-id`);
  return response.data;
};

export const createGlobalRequirement = async (projectId, requirementData) => {
  const response = await api.post(`/requirements/projects/${projectId}/requirements`, requirementData);
  return response.data;
};

export const listGlobalRequirements = async (projectId) => {
  const response = await api.get(`/requirements/projects/${projectId}/requirements`);
  return response.data;
};

export const updateGlobalRequirement = async (projectId, requirementId, requirementData) => {
  const response = await api.put(`/requirements/projects/${projectId}/requirements/${requirementId}`, requirementData);
  return response.data;
};

export const updateGlobalRequirementStatus = async (projectId, reqId, status) => {
  const response = await api.put(`/requirements/projects/${projectId}/requirements/${reqId}/status`, { status });
  return response.data;
};

export const deleteGlobalRequirement = async (projectId, reqId) => {
  await api.delete(`/requirements/projects/${projectId}/requirements/${reqId}`);
};

export const markGlobalRequirementForDeletion = async (projectId, reqId) => {
  const response = await api.post(`/requirements/projects/${projectId}/requirements/${reqId}/mark-deleted`);
  return response.data;
};

export const uncommitRequirement = async (featureId, logicalId) => {
  const response = await api.post(`/requirements/features/${featureId}/requirements/${logicalId}/uncommit`);
  return response.data;
};

export const uncommitGlobalRequirement = async (projectId, reqId) => {
  const response = await api.post(`/requirements/projects/${projectId}/requirements/${reqId}/uncommit`);
  return response.data;
};

// List features for a project
export const listFeatures = async (projectId) => {
  const response = await api.get(`/features/project/${projectId}`);
  return response.data;
};

// Get all requirements for a project (global + all features) in one efficient call
export const getAllProjectRequirements = async (projectId, signal) => {
  const response = await api.get(`/requirements/projects/${projectId}/all-requirements`, { signal });
  return response.data;
};

// Get all checkpoints with full requirements data for design document generation
export const getCheckpointsWithRequirements = async (projectId) => {
  const response = await api.get(`/requirements/projects/${projectId}/checkpoints-with-data`);
  return response.data;
};

// Get a server-rendered design document for a checkpoint (for external system consumption)
export const getRenderedDesignDocument = async (projectId, toCheckpointId, fromCheckpointId = null) => {
  const params = { to_checkpoint: toCheckpointId };
  if (fromCheckpointId) params.from_checkpoint = fromCheckpointId;
  const response = await api.get(`/requirements/projects/${projectId}/rendered-design-document`, { params });
  return response.data;
};

// Get design document templates
export const getDesignTemplates = async () => {
  const response = await api.get('/requirements/design-templates');
  return response.data;
};

// Design Documents
export const generateDesignDocument = async (projectId, documentData) => {
  const response = await api.post(`/requirements/projects/${projectId}/design-documents`, documentData);
  return response.data;
};

export const listDesignDocuments = async (projectId) => {
  const response = await api.get(`/requirements/projects/${projectId}/design-documents`);
  return response.data;
};

export const getDesignDocument = async (projectId, docId) => {
  const response = await api.get(`/requirements/projects/${projectId}/design-documents/${docId}`);
  return response.data;
};

export const exportDesignDocument = async (projectId, docId, format = 'markdown') => {
  const response = await api.get(`/requirements/projects/${projectId}/design-documents/${docId}/export`, {
    params: { format },
    responseType: 'blob'
  });
  return response.data;
};

export const downloadDocument = async (projectId, docId, format = 'markdown') => {
  const blob = await exportDesignDocument(projectId, docId, format);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `design-document.${format}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Helper functions
// Constants for requirement types and priorities
export const REQUIREMENT_PRIORITY = {
  MUST: 'MUST',
  SHOULD: 'SHOULD',
  COULD: 'COULD',
  WONT: 'WONT'
};

export const REQUIREMENT_TYPE = {
  FUNCTIONAL: 'FUNCTIONAL',
  NON_FUNCTIONAL: 'NON_FUNCTIONAL',
  CONSTRAINT: 'CONSTRAINT',
  ASSUMPTION: 'ASSUMPTION'
};

export const REQUIREMENT_STATUS = {
  DRAFT: 'DRAFT',
  READY_FOR_CHECKPOINT: 'READY_FOR_CHECKPOINT',
  COMMITTED: 'COMMITTED'
};

export const GLOBAL_REQUIREMENT_SECTION = {
  GENERAL: 'GENERAL',
  TECH_STACK: 'TECH_STACK',
  PERFORMANCE: 'PERFORMANCE',
  SECURITY_COMPLIANCE: 'SECURITY_COMPLIANCE',
  ACCESSIBILITY: 'ACCESSIBILITY',
  OTHER: 'OTHER'
};

// Helper for display names
export const getPriorityDisplay = (priority) => {
  switch (priority) {
    case REQUIREMENT_PRIORITY.MUST:
      return 'Must Have';
    case REQUIREMENT_PRIORITY.SHOULD:
      return 'Should Have';
    case REQUIREMENT_PRIORITY.COULD:
      return 'Could Have';
    case REQUIREMENT_PRIORITY.WONT:
      return "Won't Have";
    default:
      return priority;
  }
};

export const getTypeDisplay = (type) => {
  switch (type) {
    case REQUIREMENT_TYPE.FUNCTIONAL:
      return 'Functional';
    case REQUIREMENT_TYPE.NON_FUNCTIONAL:
      return 'Non-Functional';
    case REQUIREMENT_TYPE.CONSTRAINT:
      return 'Constraint';
    case REQUIREMENT_TYPE.ASSUMPTION:
      return 'Assumption';
    default:
      return type;
  }
};

export const getStatusDisplay = (status) => {
  switch (status) {
    case REQUIREMENT_STATUS.DRAFT:
      return 'Draft';
    case REQUIREMENT_STATUS.READY_FOR_CHECKPOINT:
      return 'Ready for Checkpoint';
    case REQUIREMENT_STATUS.COMMITTED:
      return 'Committed';
    default:
      return status;
  }
};

export const getSectionDisplay = (section) => {
  switch (section) {
    case GLOBAL_REQUIREMENT_SECTION.GENERAL:
      return 'General';
    case GLOBAL_REQUIREMENT_SECTION.TECH_STACK:
      return 'Tech Stack';
    case GLOBAL_REQUIREMENT_SECTION.PERFORMANCE:
      return 'Performance';
    case GLOBAL_REQUIREMENT_SECTION.SECURITY_COMPLIANCE:
      return 'Security & Compliance';
    case GLOBAL_REQUIREMENT_SECTION.ACCESSIBILITY:
      return 'Accessibility';
    case GLOBAL_REQUIREMENT_SECTION.OTHER:
      return 'Other';
    default:
      return section;
  }
};

// Status badge colors
export const getStatusColor = (status) => {
  switch (status) {
    case REQUIREMENT_STATUS.DRAFT:
      return '#9E9E9E'; // Grey
    case REQUIREMENT_STATUS.READY_FOR_CHECKPOINT:
      return '#FF9800'; // Orange
    case REQUIREMENT_STATUS.COMMITTED:
      return '#4CAF50'; // Green
    default:
      return '#9E9E9E';
  }
};

// Priority badge colors
export const getPriorityColor = (priority) => {
  switch (priority) {
    case REQUIREMENT_PRIORITY.MUST:
      return '#F44336'; // Red
    case REQUIREMENT_PRIORITY.SHOULD:
      return '#FF9800'; // Orange
    case REQUIREMENT_PRIORITY.COULD:
      return '#2196F3'; // Blue
    case REQUIREMENT_PRIORITY.WONT:
      return '#9E9E9E'; // Grey
    default:
      return '#9E9E9E';
  }
};
